import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command, CommandInput, CommandItem, CommandList } from "cmdk";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { cn } from "../utils/cx.js";
import { matchScore } from "../utils/search.js";
import { Field } from "./Field.js";

export interface SelectOption {
  label: ReactNode;
  value: string;
  disabled?: boolean;
  /** Optional text to match against when searching (defaults to label/value). */
  searchText?: string;
  /**
   * Muted secondary text on the row — the parent in a hierarchy, a code, a
   * unit. Shown in the list only; the trigger keeps just the label.
   */
  hint?: ReactNode;
  /** Indent level for hierarchical lists (see `treeSelectOptions`). */
  depth?: number;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  /** Empty-state text on the trigger; defaults to a search prompt when searchable. */
  placeholder?: string;
  /**
   * Force the search box on or off. Left unset it turns itself on once the
   * list is long enough to be worth searching (`AUTO_SEARCH_MIN_OPTIONS`), so
   * every long picker in the app is searchable without each call site opting in.
   */
  showSearch?: boolean;
  searchPlaceholder?: string;
  notFoundContent?: ReactNode;
  /** Show a clear (×) affordance; clearing reports "" so optional fields can unset. */
  allowClear?: boolean;
  /**
   * Let the user commit a value that is not in `options` by typing it — the
   * search box doubles as an input and offers "create <query>". Implies
   * showSearch. The caller keeps the typed value (adding it to `options` on the
   * next render is its job).
   */
  creatable?: boolean;
  /** Label of the create row; defaults to `"<query>" ekle`. */
  createLabel?: (query: string) => ReactNode;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  id?: string;
}

const HEIGHT = { sm: "h-8", md: "h-9", lg: "h-10" } as const;

/**
 * From this many options on, the search box appears by default. Below it the
 * whole list is visible at a glance and a search row is just noise (yes/no,
 * tiers, roles, plans — all 2–4 options); at or above it — an org list, the
 * supply-chain stages — typing beats scanning.
 */
const AUTO_SEARCH_MIN_OPTIONS = 5;

/** What a query is matched against: explicit `searchText`, else the label. */
function haystackOf(o: SelectOption): string {
  return o.searchText ?? (typeof o.label === "string" ? o.label : o.value);
}

export function Select(props: SelectProps) {
  const {
    options,
    value,
    onChange,
    showSearch,
    searchPlaceholder,
    notFoundContent,
    allowClear,
    creatable,
    createLabel,
    size = "md",
    disabled,
  } = props;
  const auto = useId();
  const id = props.id ?? auto;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);
  const invalid = props.error != null;
  const search = creatable === true || (showSearch ?? options.length >= AUTO_SEARCH_MIN_OPTIONS);
  // A searchable select advertises itself as one: the trigger carries the
  // magnifier and — when the caller left the wording to us — a "search or pick"
  // prompt, so the user knows they can type before opening it.
  const placeholder = props.placeholder ?? (search ? "Ara veya seçin…" : "Seçiniz…");
  // A creatable select must not swallow the typed value silently: offer it as a
  // row unless an option already carries exactly that value.
  const newValue = query.trim();
  const showCreate = creatable === true && newValue !== "" && !options.some((o) => o.value === newValue);

  // Filter and rank here rather than through cmdk's own filter: cmdk re-orders
  // by score with DOM surgery, which React's next commit undoes — the matching
  // rows were right but their ranking was not. Sorting the array instead keeps
  // the caller's order for equal scores (Array#sort is stable), so a
  // hierarchical list stays parent-then-children until a query says otherwise.
  const visible = useMemo(() => {
    if (!search || query.trim() === "") return options;
    return options
      .map((o) => ({ o, score: matchScore(haystackOf(o), query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.o);
  }, [options, query, search]);

  // Each open starts from a clean search box.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const commit = (next: string) => {
    onChange?.(next);
    setOpen(false);
  };

  const control = (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            "@container flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
            HEIGHT[size],
            invalid &&
              "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
            props.className,
          )}
        >
          {/* The magnifier advertises the search, but in a dense grid cell it
              would eat the label instead — under ~14rem of trigger the chevron
              alone has to carry the affordance. */}
          {search && <Search className="size-4 shrink-0 opacity-50 @max-[14rem]:hidden" />}
          <span
            className={cn("min-w-0 flex-1 truncate text-start", selected == null && "text-muted-foreground")}
          >
            {selected ? selected.label : placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {allowClear && selected != null && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Temizle"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.("");
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </span>
            )}
            {/* One big chevron rather than the ⇅ pair: a select opens downward,
                and it turns over while open to say which way it goes. */}
            <ChevronDown className={cn("size-5 opacity-60 transition-transform", open && "rotate-180")} />
          </span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // At least as wide as the trigger, but free to grow for long rows —
          // a narrow control (the header org switcher) must not force every
          // option to truncate. Capped so it never runs off-screen.
          style={{
            minWidth: "var(--radix-popover-trigger-width)",
            maxWidth: "min(26rem, var(--radix-popover-content-available-width))",
          }}
          className="z-[1200] overflow-hidden rounded-md border bg-popover p-0 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          {/* `visible` is already filtered and ranked (accent- and Turkish-folding:
              "turkiye" finds "Türkiye", "inci amb" finds "İnci Ambalaj"), so cmdk
              only provides keyboard navigation and selection here. */}
          <Command shouldFilter={false}>
            {search && (
              // No magnifier here: the trigger sits right above with one, and a
              // second icon 4px below it just reads as a duplicate. The caret
              // lines up with the option labels instead.
              <div className="flex items-center border-b px-1">
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  placeholder={searchPlaceholder ?? "Ara…"}
                  className="h-9 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <CommandList className="max-h-64 overflow-y-auto p-1">
              {/* The create row is the answer to "no results" — never show both. */}
              {visible.length === 0 && !showCreate && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {notFoundContent ?? "Sonuç bulunamadı"}
                </div>
              )}
              {visible.map((o, i) => {
                return (
                  <CommandItem
                    key={o.value || `#${i}`}
                    // cmdk keys its own state by value; keep it unique and
                    // non-empty even for a legitimate "" row (an "all" option).
                    value={o.value || haystackOf(o) || `#${i}`}
                    disabled={o.disabled}
                    onSelect={() => commit(o.value)}
                    style={o.depth ? { paddingInlineStart: 8 + o.depth * 14 } : undefined}
                    className="flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{o.label}</span>
                      {o.hint != null && (
                        <span className="truncate text-xs text-muted-foreground">{o.hint}</span>
                      )}
                    </span>
                    {value === o.value && <Check className="size-4 shrink-0" />}
                  </CommandItem>
                );
              })}
              {showCreate && (
                // forceMount: cmdk filters by its own scoring, and the create
                // row must stay reachable for a query nothing else matches.
                <CommandItem
                  forceMount
                  value={newValue}
                  onSelect={() => commit(newValue)}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                >
                  <Plus className="size-4 shrink-0 opacity-70" />
                  <span className="truncate">
                    {createLabel ? createLabel(newValue) : `"${newValue}" ekle`}
                  </span>
                </CommandItem>
              )}
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );

  if (props.label != null || props.help != null || props.error != null) {
    return (
      <Field label={props.label} required={props.required} help={props.help} error={props.error} htmlFor={id}>
        {control}
      </Field>
    );
  }
  return control;
}
